import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import classNames from "classnames";

import {
  CanonicalDataType,
  isMessagePathAttr,
  MessagePathMetadataWellKnown,
} from "@/shared/domain/topics";
import type { PlotPanelState } from "@/shared/state/visualization";
import {
  Duration,
  nanoSecToDate,
  nanoSecToLocalTimestamp,
  nanoSecToSecStr,
} from "@/shared/time";

import {
  elapsedTimeFormatter,
  TimeDisplayMode,
  useTimeDisplayMode,
} from "../../../WorkspaceCtx";
import { resolveCategoryLabel } from "../../resolveCategoryLabel";
import type { HoverDatum } from "../PlotRenderer";

import styles from "./Tooltip.module.css";

export interface HoverData {
  data: HoverDatum[];
  x: number;
  y: number;
}

interface TooltipBodyProps {
  hoverData: HoverData | null;
  state: PlotPanelState;
}

function getDisplayValue(datum: HoverDatum, state: PlotPanelState): string {
  // Find the corresponding series in the state
  const series = state.data.find((s) => datum.datasetId.startsWith(s.id));
  if (!series) {
    return String(datum.y);
  }

  // Check if this is categorical data
  const finalMsgPathPart = series.data.messagePath.parts.at(-1);
  const dataType = isMessagePathAttr(finalMsgPathPart)
    ? finalMsgPathPart.dataType
    : CanonicalDataType.Unknown;

  if (dataType !== CanonicalDataType.Categorical) {
    return String(datum.y);
  }

  // Get the categories array from metadata
  const categories = series.data.messagePath.metadata[
    MessagePathMetadataWellKnown.Categories
  ] as unknown[];

  if (!Array.isArray(categories) || categories.length === 0) {
    return String(datum.y);
  }

  return (
    resolveCategoryLabel(datum.y, categories as string[]) ?? String(datum.y)
  );
}

function getDisplayTime(datum: HoverDatum, mode: TimeDisplayMode): string {
  switch (mode) {
    case TimeDisplayMode.Elapsed:
      return datum.x.toFixed(3);
    case TimeDisplayMode.ElapsedTime: {
      // datum.x is in seconds (elapsed)
      if (!elapsedTimeFormatter) {
        return datum.x.toFixed(3);
      }
      const duration = Duration.from({ seconds: datum.x });
      return elapsedTimeFormatter.format(duration.toObject());
    }
    case TimeDisplayMode.Log:
      return nanoSecToSecStr(datum.logTime);
    case TimeDisplayMode.UTC:
      return nanoSecToDate(datum.logTime).toISOString();
    case TimeDisplayMode.Local:
      return nanoSecToLocalTimestamp(datum.logTime);
  }
}

export function TooltipBody({ hoverData, state }: TooltipBodyProps) {
  const [timeDisplayMode, _] = useTimeDisplayMode();

  return (
    <Box
      bgcolor={(theme) => alpha(theme.palette.paper.main, 0.8)}
      className={classNames(styles.container, {
        [styles.hidden]: hoverData === null || hoverData.data.length === 0,
      })}
      color={(theme) => theme.palette.text.primary}
      component="ul"
      fontFamily={(theme) => theme.typography.caption.fontFamily}
      fontSize={(theme) => theme.typography.caption.fontSize}
      left={hoverData?.x}
      top={hoverData?.y}
    >
      {hoverData?.data.map((datum) => {
        const displayValue = getDisplayValue(datum, state);
        const displayTime = getDisplayTime(datum, timeDisplayMode);
        return (
          <li key={datum.datasetId}>
            <span
              className={styles.colorBox}
              style={{ backgroundColor: datum.color }}
            />
            {displayTime}, {displayValue}
          </li>
        );
      })}
    </Box>
  );
}
